import type { RawDraftContentState } from '@ms/yammer-content-support';
import type {
  MessageSenderBadge,
  PraiseBadge,
  ReactionType,
  StorylineAnnouncementAudience,
  StorylineAnnouncementNotificationTarget,
  ThreadMessageLevel,
} from '@ms/yammer-graphql';

import type {
  ActorReference,
  AnnouncementNotificationTargetEntityReference,
  AttachmentReference,
  Entity,
  EntityEdge,
  EntityMap,
  Group,
  GroupAgentReference,
  GroupReference,
  MessageCampaignReference,
  Network,
  NetworkReference,
  SharedMessage,
  Thread,
  TopicReference,
  User,
  UserReference,
} from '../types';

export type MessageType = 'normal' | 'system' | 'poll' | 'praise';

export type MessageSubtype =
  | 'created_group'
  | 'created_network'
  | 'network_welcome'
  | 'added_participant'
  | 'removed_participant'
  | 'pm_to_group'
  | 'group_to_group'
  | 'local_network_policy_changed'
  | 'foreign_network_policy_changed'
  | 'poll_closed'
  | 'poll_reopened'
  | 'thread_closed'
  | 'thread_reopened'
  | 'changed_to_question'
  | 'changed_to_message'
  | 'export_complete';

export type MessageReference =
  | MessageCampaignReference
  | GroupReference
  | GroupAgentReference
  | UserReference
  | TopicReference
  | NetworkReference;

export type CommonMessageFields =
  | 'id'
  | 'telemetryId'
  | 'threadId'
  | 'sender'
  | 'reactionsTotalCount'
  | 'reactionsCounts'
  | 'content';

export type CommonMessage = Pick<Message, CommonMessageFields>;

export type AttachmentsType = AttachmentReference['schema'] | 'mixed' | null;

export interface BaseMessageContent {
  readonly type: MessageType;
}

export interface ContentStateMessage extends BaseMessageContent {
  readonly body: RawDraftContentState;
  readonly language?: string;
}

export interface NormalMessageContent extends ContentStateMessage {
  readonly type: 'normal';
  readonly title?: string;
  readonly references?: MessageReference[];
}

export interface PollMessageContent extends ContentStateMessage {
  readonly type: 'poll';
  readonly answers: PollAnswer[];
  readonly isPollClosed: boolean;
  readonly viewerCanClosePoll: boolean;
}

export interface PollAnswer {
  readonly text: string;
  readonly index: number;
  readonly isSelected: boolean;
  readonly voteCount: number;
}

export interface PraiseMessageContent extends ContentStateMessage {
  readonly badge: PraiseBadge;
  readonly type: 'praise';
  readonly users: User['id'][];
}

export interface SystemMessageContent extends ContentStateMessage {
  readonly type: 'system';
  readonly references: MessageReference[];
}

export interface AddedParticipantMessageContent extends SystemMessageContent {
  readonly addedParticipantIds: User['id'][];
}

export interface CreatedGroupMessageContent extends SystemMessageContent {
  readonly createdGroupId: Group['id'];
}

export interface RemovedParticipantMessageContent extends SystemMessageContent {
  readonly removedParticipantIds: User['id'][];
}

export interface MovedGroupThreadToGroupMessageContent extends SystemMessageContent {
  readonly viewerHasAccessToSourceGroup: boolean;
  readonly sourceGroupId?: Group['id'];
  readonly viewerHasAccessToDestinationGroup: boolean;
  readonly destinationGroupId?: Group['id'];
}

export interface MovedDirectMessageToGroupMessageContent extends SystemMessageContent {
  readonly destinationGroupId: Group['id'];
}

export interface JoinedNetworkSystemMessageContent extends SystemMessageContent {
  readonly joinedNetworkId: Network['id'];
  readonly joinedUserId: User['id'];
}

export interface DisabledForeignNetworksMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface RemovedForeignNetworkSystemMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface ClosedPollVotingSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface ReopenedPollVotingSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface CreatedNetworkSystemMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface MarkedMessageAsQuestionSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface UnmarkedMessageAsQuestionSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export const isNormalMessageContent = (content: Message['content']): content is NormalMessageContent =>
  content.type === 'normal';

export const isSystemMessageContent = (content: Message['content']): content is SystemMessageContent =>
  content.type === 'system';

export const isPollMessageContent = (content: Message['content']): content is PollMessageContent =>
  content.type === 'poll';

export const isPraiseMessageContent = (content: Message['content']): content is PraiseMessageContent =>
  content.type === 'praise';

export const isAddedParticipantsContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is AddedParticipantMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'added_participant';

export const isCreatedGroupContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is CreatedGroupMessageContent => isSystemMessageContent(content) && messageSubtype === 'created_group';

export const isMovedGroupThreadToGroupContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is MovedGroupThreadToGroupMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'group_to_group';

export const isMovedDirectMessageToGroupContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is MovedDirectMessageToGroupMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'pm_to_group';

export const isRemovedParticipantContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is RemovedParticipantMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'removed_participant';

export const isJoinedNetworkContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is JoinedNetworkSystemMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'network_welcome';

export const isDisabledForeignNetworksContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is DisabledForeignNetworksMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'local_network_policy_changed';

export const isRemovedForeignNetworkContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is RemovedForeignNetworkSystemMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'foreign_network_policy_changed';

export const isClosedThreadContent = (content: Message['content'], messageSubtype: Message['subtype']) =>
  isSystemMessageContent(content) && messageSubtype === 'thread_closed';

export const isReopenedThreadContent = (content: Message['content'], messageSubtype: Message['subtype']) =>
  isSystemMessageContent(content) && messageSubtype === 'thread_reopened';

export const isClosedPollVotingContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is ClosedPollVotingSystemMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'poll_closed';

export const isReopenedPollVotingContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is ReopenedPollVotingSystemMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'poll_reopened';

export const isCreatedNetworkContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is CreatedNetworkSystemMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'created_network';

export const isMarkedAsQuestionContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is MarkedMessageAsQuestionSystemMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'changed_to_question';

export const isUnmarkedAsQuestionContent = (
  content: Message['content'],
  messageSubtype: Message['subtype']
): content is UnmarkedMessageAsQuestionSystemMessageContent =>
  isSystemMessageContent(content) && messageSubtype === 'changed_to_message';

type GetTitleFromMessageMessageContent = (content: Message['content']) => string | undefined;
export const getTitleFromMessageContent: GetTitleFromMessageMessageContent = (content) =>
  isNormalMessageContent(content) ? content.title : undefined;

export type MessageContent =
  | NormalMessageContent
  | PollMessageContent
  | PraiseMessageContent
  | SystemMessageContent
  | MovedGroupThreadToGroupMessageContent;

export type MessageReactionsCounts = Record<ReactionType, number>;

export type MessageFeaturedReactions = Record<ReactionType, User['id'][]>;

export interface Message extends Entity {
  readonly viewerMutationId: string;
  readonly telemetryId: string;
  readonly threadId: Thread['id'];
  readonly announcementAudience?: StorylineAnnouncementAudience;
  readonly announcementAudienceIds?: string[];
  readonly announcementTemporaryAudienceIds?: string[];
  readonly announcementNotificationTargets?: StorylineAnnouncementNotificationTarget[];
  readonly announcementNotificationTargetEntityReferences?: AnnouncementNotificationTargetEntityReference[];
  readonly sender: ActorReference;
  readonly senderBadges?: MessageSenderBadge[];
  readonly featuredReactionUserIds: User['id'][];
  readonly featuredUserIdsByReaction: MessageFeaturedReactions;
  readonly featuredQuestionUpvoteUserIds: User['id'][];
  readonly upvotesTotalCount: number;
  readonly reactionsTotalCount: number;
  readonly reactionsCounts: MessageReactionsCounts;
  readonly viewerHasLiked: boolean;
  readonly viewerHasUpvoted: boolean;
  readonly viewerReaction?: ReactionType;
  readonly subtype?: MessageSubtype;
  readonly content: MessageContent;
  readonly isEdited: boolean;
  readonly isEditable: boolean;
  readonly isDeleted: boolean;
  readonly isDeletable: boolean;
  readonly isDraft: boolean;
  readonly scheduledPublishAt?: number;
  readonly isQuestion: boolean;
  readonly isArticle: boolean;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly attachments: AttachmentReference[];
  readonly attachmentsTotalCount: number;
  readonly sharedMessageId?: SharedMessage['id'];
  readonly hasDeletedSharedMessage: boolean;
  readonly threadLevel?: ThreadMessageLevel;
  readonly parentMessageId?: Message['id'];
  readonly hasFailedFileDeletions: boolean;
  readonly viewerCanShare: boolean;
  readonly availableTranslations: string[];
  readonly defaultLanguage?: string;
}

export type FeaturedReactionFields =
  | 'id'
  | 'reactionsTotalCount'
  | 'reactionsCounts'
  | 'featuredReactionUserIds'
  | 'viewerHasLiked'
  | 'viewerReaction';

export type FeaturedMessageReactions = Pick<Message, FeaturedReactionFields>;

export type Messages = EntityMap<Message>;

export type MessageEdge = EntityEdge<Message>;

export interface ReplyEdge {
  readonly cursor?: string;
  readonly nodeId: Message['id'];
  readonly senderId: User['id'];
  readonly sortKey: string;
  readonly viewerHasSeen: boolean;
}

export interface RecommendedToplevelReplyEdge {
  readonly nodeId: Message['id'];
  readonly viewerHasSeen: boolean;
}
